/* EcoCocoon FAQ category pages.

   Progressive enhancement only: with JavaScript off the questions still render
   and open, and the search field is simply inert.

   The searchable text is read out of the DOM the first time someone types,
   rather than being duplicated into a data attribute on every question. On the
   Drinkware page that keeps roughly 40KB of answer text out of the HTML. */
(function () {
  'use strict';

  /* ---- sticky category bar ---------------------------------------------
     Publishes the bar's height so the rail and jump links clear it, marks it
     once it is pinned (for the shadow), and brings the current category chip
     into view when the row scrolls sideways on small screens. */
  function initCatnav(root) {
    var nav = root.querySelector('.faq-catnav');
    if (!nav) return;

    function setHeight() {
      root.style.setProperty('--faq-catnav-h', nav.offsetHeight + 'px');
    }
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(nav);

    /* A 1px marker sits where the bar would be unpinned. Once the page scrolls
       it above the bar, the bar is pinned. One rect read per frame, at most. */
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    /* display is set inline: the theme hides every empty div */
    sentinel.style.cssText = 'display:block;height:1px;margin-bottom:-1px;pointer-events:none;';
    nav.parentNode.insertBefore(sentinel, nav);
    var ticking = false;
    function checkStuck() {
      ticking = false;
      nav.classList.toggle('is-stuck', sentinel.getBoundingClientRect().bottom < nav.getBoundingClientRect().top - 0.5);
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(checkStuck); }
    }, { passive: true });
    checkStuck();

    var current = nav.querySelector('.is-current');
    var scroller = nav.querySelector('.faq-catnav__scroller');
    if (current && scroller && scroller.scrollWidth > scroller.clientWidth) {
      scroller.scrollLeft = current.offsetLeft - (scroller.clientWidth - current.offsetWidth) / 2;
    }
  }

  function initFaq(root) {
    initCatnav(root);
    var input = root.querySelector('[data-faq-search]');
    var clear = root.querySelector('[data-faq-clear]');
    var status = root.querySelector('[data-faq-status]');
    var noResults = root.querySelector('[data-faq-noresults]');
    var sections = Array.prototype.slice.call(root.querySelectorAll('[data-faq-section]'));
    var index = null;

    /* ---- search ---------------------------------------------------------
       Matches question and answer text. Sections with no surviving question are
       hidden so the page does not show empty headings. */

    // Built once, on the first keystroke, so an untouched page pays nothing.
    function buildIndex() {
      index = sections.map(function (section) {
        return {
          el: section,
          items: Array.prototype.map.call(
            section.querySelectorAll('[data-faq-item]'),
            function (item) {
              return { el: item, text: (item.textContent || '').toLowerCase() };
            }
          )
        };
      });
    }

    function search(raw) {
      var query = (raw || '').trim().toLowerCase();

      if (clear) clear.hidden = query === '';
      if (!index) buildIndex();

      if (!query) {
        index.forEach(function (section) {
          section.el.hidden = false;
          section.items.forEach(function (item) {
            item.el.hidden = false;
            item.el.open = false;
          });
        });
        if (noResults) noResults.hidden = true;
        if (status) status.textContent = '';
        return;
      }

      var matches = 0;

      index.forEach(function (section) {
        var found = 0;

        section.items.forEach(function (item) {
          var hit = item.text.indexOf(query) !== -1;
          item.el.hidden = !hit;
          item.el.open = hit;
          if (hit) found++;
        });

        section.el.hidden = found === 0;
        matches += found;
      });

      if (noResults) noResults.hidden = matches !== 0;

      if (status) {
        status.textContent = matches === 0
          ? 'No results for "' + raw.trim() + '"'
          : matches + (matches === 1 ? ' question found' : ' questions found');
      }
    }

    if (input) {
      input.addEventListener('input', function () { search(input.value); });

      // Enter in a search field would otherwise submit an enclosing form.
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape' && input.value) {
          input.value = '';
          search('');
        }
      });
    }

    if (clear && input) {
      clear.addEventListener('click', function () {
        input.value = '';
        search('');
        input.focus();
      });
    }

    /* ---- section navigation --------------------------------------------
       Highlights whichever section is currently in view. Purely decorative, so
       it is skipped where IntersectionObserver is unavailable. */
    var navLinks = Array.prototype.slice.call(root.querySelectorAll('[data-faq-navlink]'));

    if (navLinks.length && 'IntersectionObserver' in window) {
      var linkFor = {};
      navLinks.forEach(function (link) {
        linkFor[link.getAttribute('data-faq-navlink')] = link;
      });

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var link = linkFor[entry.target.id];
          if (!link) return;
          navLinks.forEach(function (other) { other.classList.remove('is-active'); });
          link.classList.add('is-active');
        });
      }, { rootMargin: '-15% 0px -70% 0px', threshold: 0 });

      sections.forEach(function (section) { observer.observe(section); });
    }

    /* ---- deep links -----------------------------------------------------
       Arriving at #some-section opens its first question so the visitor lands
       on an answer rather than a wall of collapsed rows.

       Load only, deliberately. Doing this on hashchange as well would open a
       question while the browser is still smooth-scrolling to a section the
       visitor just clicked in the contents list, which moves the target
       underneath the scroll and leaves the heading above the top of the
       screen. */
    if (window.location.hash) {
      var target = null;
      try {
        target = root.querySelector(window.location.hash);
      } catch (error) {
        target = null;
      }
      if (target && target.hasAttribute('data-faq-section')) {
        var first = target.querySelector('[data-faq-item]');
        if (first) first.open = true;
      }
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-faq-root]'), initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();